import { useState, useCallback, useRef } from 'react'

const MAX_HISTORY_SIZE = 50

export interface HistoryState<T> {
  past: T[]
  present: T
  future: T[]
}

export interface UseHistoryReturn<T> {
  state: T
  setState: (newState: T | ((prev: T) => T)) => void
  undo: () => void
  redo: () => void
  canUndo: boolean
  canRedo: boolean
  clearHistory: () => void
}

/**
 * Custom hook for managing state with undo/redo history
 * @param initialState - The initial state value
 * @param isEqual - Optional function to compare states (defaults to deep comparison)
 */
export function useHistory<T>(
  initialState: T,
  isEqual?: (a: T, b: T) => boolean
): UseHistoryReturn<T> {
  const [history, setHistory] = useState<HistoryState<T>>({
    past: [],
    present: initialState,
    future: [],
  })

  // Track if we're in the middle of an undo/redo operation
  const isUndoRedo = useRef(false)

  // Default equality check (shallow for primitives, JSON for objects)
  const checkEqual = useCallback(
    (a: T, b: T): boolean => {
      if (isEqual) return isEqual(a, b)
      if (a === b) return true
      if (typeof a !== 'object' || typeof b !== 'object') return false
      return JSON.stringify(a) === JSON.stringify(b)
    },
    [isEqual]
  )

  const setState = useCallback(
    (newState: T | ((prev: T) => T)) => {
      setHistory((prev) => {
        const resolvedState =
          typeof newState === 'function'
            ? (newState as (prev: T) => T)(prev.present)
            : newState

        // Don't add to history if state hasn't changed
        if (checkEqual(prev.present, resolvedState)) {
          return prev
        }

        // If this is an undo/redo operation, don't add to history
        if (isUndoRedo.current) {
          isUndoRedo.current = false
          return {
            ...prev,
            present: resolvedState,
          }
        }

        // Add current state to past and clear future
        const newPast = [...prev.past, prev.present]

        // Limit history size
        if (newPast.length > MAX_HISTORY_SIZE) {
          newPast.shift()
        }

        return {
          past: newPast,
          present: resolvedState,
          future: [], // Clear future on new action
        }
      })
    },
    [checkEqual]
  )

  const undo = useCallback(() => {
    setHistory((prev) => {
      if (prev.past.length === 0) return prev

      const newPast = [...prev.past]
      const newPresent = newPast.pop()!

      isUndoRedo.current = true

      return {
        past: newPast,
        present: newPresent,
        future: [prev.present, ...prev.future],
      }
    })
  }, [])

  const redo = useCallback(() => {
    setHistory((prev) => {
      if (prev.future.length === 0) return prev

      const newFuture = [...prev.future]
      const newPresent = newFuture.shift()!

      isUndoRedo.current = true

      return {
        past: [...prev.past, prev.present],
        present: newPresent,
        future: newFuture,
      }
    })
  }, [])

  const clearHistory = useCallback(() => {
    setHistory((prev) => ({
      past: [],
      present: prev.present,
      future: [],
    }))
  }, [])

  return {
    state: history.present,
    setState,
    undo,
    redo,
    canUndo: history.past.length > 0,
    canRedo: history.future.length > 0,
    clearHistory,
  }
}
